package com.example.mystybag.appCore.logic.addMerchantLocation

import com.example.mystybag.appCore.model.addMerchantLocation.BusinessCategoryItem
import com.example.mystybag.appCore.model.addMerchantLocation.MerchantRegistrationAddDescriptionUIState
import com.example.mystybag.appCore.model.addMerchantLocation.ProductRowUIModel
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep9
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep9B
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetBusinessCategoriesRequestModel
import kotlinx.coroutines.launch

class MerchantRegistrationAddDescriptionViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep9
) : ChildViewModel<MerchantRegistrationAddDescriptionUIState>(context, MerchantRegistrationAddDescriptionUIState()) {

    init {
        val initialRows = parseWhatYouCouldGet(config.whatYouCouldGet)
        updateState {
            it.copy(
                productRows = initialRows
            )
        }
    }

    override fun onCreate() {
        navControls.backButtonVisible = true
        loadBusinessCategories()
    }

    private fun loadBusinessCategories() {
        scope.launch {
            updateState { it.copy(isLoading = true) }
            commandBlock(showProgress = false) {
                CommonCommands.getBusinessCategories(GetBusinessCategoriesRequestModel())
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                // Remove duplicates by ID to avoid key conflicts in LazyColumn
                                val uniqueCategories = response.categories
                                    .distinctBy { it.id }
                                    .map { cat ->
                                        BusinessCategoryItem(
                                            id = cat.id,
                                            title = cat.title,
                                            description = cat.description,
                                            coverImageUrls = cat.coverImageUrls.filter { it.isNotBlank() }
                                        )
                                    }
                                val preSelected = config.businessCategoryId?.let { id ->
                                    uniqueCategories.find { it.id == id }
                                }
                                updateState {
                                    val newSelected = it.selectedBusinessCategory ?: preSelected
                                    val matchedPreset = indexOfMatchingCoverPreset(config.existingCoverPhotoUrl, newSelected)
                                    it.copy(
                                        businessCategories = uniqueCategories,
                                        isLoading = false,
                                        // Prefer user selection if they picked before this request finished (avoid race with config pre-fill).
                                        selectedBusinessCategory = newSelected,
                                        selectedPreviewImageIndex = matchedPreset ?: it.selectedPreviewImageIndex
                                    )
                                }
                            }
                        },
                        baseError = {
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                            }
                        }
                    )
            }
        }
    }

    fun showBusinessCategoryPicker() {
        updateState { it.copy(showBusinessCategoryPicker = true) }
    }

    fun hideBusinessCategoryPicker() {
        updateState { it.copy(showBusinessCategoryPicker = false) }
    }

    fun selectBusinessCategory(categoryId: Long, _categoryName: String) {
        val businessCategory = uiState.value.businessCategories.find { it.id == categoryId } ?: run {
            updateState { it.copy(showBusinessCategoryPicker = false) }
            return
        }
        val sameCategory = uiState.value.selectedBusinessCategory?.id == categoryId
        val nextPresetIndex = when {
            sameCategory -> uiState.value.selectedPreviewImageIndex
            else -> indexOfMatchingCoverPreset(config.existingCoverPhotoUrl, businessCategory)
        }
        updateState {
            it.copy(
                selectedBusinessCategory = businessCategory,
                selectedPreviewImageIndex = nextPresetIndex,
                showBusinessCategoryPicker = false,
                requiredBusinessCategoryError = false,
                requiredCoverPreviewError = false
            )
        }
    }

    fun updateProductName(index: Int, text: String) {
        if (index !in uiState.value.productRows.indices) return
        val sanitized = sanitizeInputForEditing(text).take(80)
        updateState {
            it.copy(
                productRows = it.productRows.toMutableList().apply {
                    this[index] = this[index].copy(productName = sanitized)
                },
                requiredProductNameRows = it.requiredProductNameRows - index
            )
        }
    }

    fun updateGrams(index: Int, text: String) {
        if (index !in uiState.value.productRows.indices) return
        val grams = text.filter { char -> char.isDigit() }.take(6)
        updateState {
            it.copy(
                productRows = it.productRows.toMutableList().apply {
                    this[index] = this[index].copy(grams = grams)
                },
                requiredProductGramsRows = it.requiredProductGramsRows - index
            )
        }
    }

    fun addProductRow() {
        updateState { state ->
            if (state.productRows.size >= 20) state
            else state.copy(productRows = state.productRows + ProductRowUIModel())
        }
    }

    fun removeProductRow(index: Int) {
        updateState { state ->
            if (index !in state.productRows.indices || state.productRows.size <= 1) return@updateState state
            val updatedRows = state.productRows.filterIndexed { i, _ -> i != index }
            val updatedNameErrors = state.requiredProductNameRows
                .filter { it != index }
                .map { if (it > index) it - 1 else it }
                .toSet()
            val updatedGramsErrors = state.requiredProductGramsRows
                .filter { it != index }
                .map { if (it > index) it - 1 else it }
                .toSet()
            state.copy(
                productRows = updatedRows,
                requiredProductNameRows = updatedNameErrors,
                requiredProductGramsRows = updatedGramsErrors
            )
        }
    }

    fun selectPreviewImage(index: Int) {
        updateState { state ->
            state.copy(selectedPreviewImageIndex = index, requiredCoverPreviewError = false)
        }
    }

    private fun needsCoverPresetSelection(): Boolean =
        config.existingCoverPhotoUrl.isNullOrBlank()

    fun onContinue() {
        val state = uiState.value
        val invalidNameRows = state.productRows.mapIndexedNotNull { index, row ->
            index.takeIf { row.productName.isBlank() }
        }.toSet()
        val invalidGramsRows = state.productRows.mapIndexedNotNull { index, row ->
            index.takeIf { row.grams.isBlank() }
        }.toSet()
        val missingBusinessCategory = state.selectedBusinessCategory == null
        val presetUrls = state.selectedBusinessCategory?.coverImageUrls?.filter { it.isNotBlank() } ?: emptyList()
        val missingCoverPreset = needsCoverPresetSelection() &&
            (presetUrls.isEmpty() ||
                state.selectedPreviewImageIndex == null ||
                state.selectedPreviewImageIndex !in presetUrls.indices)
        if (missingBusinessCategory || missingCoverPreset || invalidNameRows.isNotEmpty() || invalidGramsRows.isNotEmpty()) {
            updateState {
                it.copy(
                    requiredBusinessCategoryError = missingBusinessCategory,
                    requiredCoverPreviewError = missingCoverPreset,
                    requiredProductNameRows = invalidNameRows,
                    requiredProductGramsRows = invalidGramsRows,
                    validationRequestId = it.validationRequestId + 1
                )
            }
            if (missingBusinessCategory) {
                showBusinessCategoryPicker()
            }
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }

        val whatYouCouldGetPayload = buildWhatYouCouldGetPayload(state.productRows)
        val selectedCategory = state.selectedBusinessCategory
        if (whatYouCouldGetPayload.isNotBlank() && selectedCategory != null && !state.isLoading) {
            val coverUrls = selectedCategory.coverImageUrls.filter { it.isNotBlank() }
            val selectedCoverUrl = state.selectedPreviewImageIndex
                ?.takeIf { it in coverUrls.indices }
                ?.let { coverUrls[it] }
            scope.launch {
                launchOnMain {
                    navigate(
                        NavigatorMerchantRegistrationStep9B(
                            merchantId = config.merchantId,
                            storeName = config.storeName,
                            storeAddress = config.storeAddress,
                            cityId = config.cityId,
                            cityName = config.cityName,
                            countryId = config.countryId,
                            countryName = config.countryName,
                            storePhoneNumber = config.storePhoneNumber,
                            dialCode = config.dialCode,
                            packaging = config.packaging,
                            additionalPickupInstructions = config.additionalPickupInstructions,
                            surpriseBagContainsBuffetFood = config.surpriseBagContainsBuffetFood,
                            latitude = config.latitude,
                            longitude = config.longitude,
                            whatYouCouldGet = whatYouCouldGetPayload,
                            businessCategoryId = selectedCategory.id,
                            locationCoverImageFileName = null,
                            selectedPresetCoverImageUrl = selectedCoverUrl,
                            returnToDashboard = config.returnToDashboard,
                            locationId = config.locationId,
                            existingCoverPhotoUrl = config.existingCoverPhotoUrl,
                            existingMenuPhotoUrls = config.existingMenuPhotoUrls
                        )
                    )
                }
            }
        }
    }

    private fun parseWhatYouCouldGet(rawValue: String): List<ProductRowUIModel> {
        if (rawValue.isBlank()) return listOf(ProductRowUIModel())
        val parsedRows = rawValue
            .split(";")
            .mapNotNull { token ->
                val cleanedToken = token.trim()
                if (cleanedToken.isBlank()) return@mapNotNull null

                val parts = cleanedToken.split(",", limit = 2)
                val name = parts.getOrNull(0)?.trim().orEmpty()
                val grams = parts.getOrNull(1)?.trim().orEmpty().filter { it.isDigit() }
                ProductRowUIModel(
                    productName = sanitizeInputForPayload(name).take(80),
                    grams = grams.take(6)
                )
            }
            .filter { it.productName.isNotBlank() || it.grams.isNotBlank() }

        return if (parsedRows.isNotEmpty()) parsedRows else listOf(ProductRowUIModel())
    }

    private fun buildWhatYouCouldGetPayload(rows: List<ProductRowUIModel>): String =
        rows.mapNotNull { row ->
            val productName = sanitizeInputForPayload(row.productName)
            val grams = row.grams.filter { it.isDigit() }
            if (productName.isBlank() || grams.isBlank()) null
            else "$productName,$grams"
        }.joinToString(";")

    /**
     * Keep user-entered spacing while editing (e.g. trailing space), but still block separators
     * used in backend payload format.
     */
    private fun sanitizeInputForEditing(value: String): String =
        value
            .replace(";", "")
            .replace(",", "")

    private fun sanitizeInputForPayload(value: String): String =
        value
            .replace(";", "")
            .replace(",", "")
            .trim()
}

